const DEFAULT_REMOTE_BASE_URL = 'https://www.symfoni.dev';
const DEFAULT_DEV_GEONORGE_DEMO_URL = 'http://localhost:5174/';
const DEFAULT_BUILD_GEONORGE_DEMO_URL = '/geonorge/';
const DEFAULT_MOCK_EXPIRES_MS = 5 * 60 * 1000;
const DEFAULT_MOCK_COMPLETE_DELAY_MS = 2500;
const DEFAULT_LOG_LEVEL = 'info';
const LOCAL_DEV_API_PROXY_BASE = '/api/symfoni-demo';
const BRREG_PROXY_BASE = '/api/brreg';
const ENHETSREGISTER_PROXY_BASE = '/api/enhetsregisteret';
const STOTTEREGISTER_SEARCH_PATH = '/api/brreg/soek/stoettetildeling';
const EAID_REGISTER_API_BASE = '/api/eaid';

const VALID_APP_ENVIRONMENTS = ['development', 'test', 'staging', 'production'] as const;
const VALID_LOG_LEVELS = ['debug', 'info', 'warn', 'error'] as const;

type ValidAppEnvironment = (typeof VALID_APP_ENVIRONMENTS)[number];
type ValidLogLevel = (typeof VALID_LOG_LEVELS)[number];

export type AppVariant =
  | 'oslo'
  | 'english'
  | 'oslo-circular-fast-track'
  | 'innovasjon-norge'
  | 'nordic-smart-city-v2'
  | 'geonorge'
  | 'iga-geonorge'
  | 'hmsreg';
export type SymfoniApiMode = 'mock' | 'remote';
export type AppEnvironment = ValidAppEnvironment;
export type LogLevel = ValidLogLevel;

export interface RuntimeConfig {
  app: {
    variant: AppVariant;
    locale: string;
    environment: AppEnvironment;
    version: string;
    basePath: string;
  };
  network: {
    useDevProxy: boolean;
    corsMode: 'vite-proxy' | 'direct';
    brregBasePath: string;
    enhetsregisterBasePath: string;
    stotteregisterSearchPath: string;
    eAidRegisterApiBasePath: string;
  };
  symfoni: {
    apiMode: SymfoniApiMode;
    remoteBaseUrl: string;
    demoApiBase: string;
    enableNgrokSkipHeader: boolean;
    mockExpiresMs: number;
    mockCompleteDelayMs: number;
  };
  geonorge: {
    demoUrl: string;
  };
  observability: {
    telemetryEnabled: boolean;
    telemetryEndpoint?: string;
    logLevel: LogLevel;
  };
  warnings: string[];
}

interface RuntimeInitOptions {
  variant: AppVariant;
  locale: string;
}

let activeRuntimeConfig: RuntimeConfig | null = null;

function isLocalhost(hostname: string): boolean {
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]';
}

function normalizeBasePath(rawBasePath?: string): string {
  if (!rawBasePath) {
    return '/';
  }

  const trimmed = rawBasePath.trim();
  if (!trimmed || trimmed === '/') {
    return '/';
  }

  const withLeadingSlash = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  return withLeadingSlash.endsWith('/') ? withLeadingSlash : `${withLeadingSlash}/`;
}

function normalizeAppPath(path: string): string {
  if (!path || path.trim() === '') {
    return '/';
  }

  return path.startsWith('/') ? path : `/${path}`;
}

function normalizeUrlWithoutTrailingSlash(rawValue: string): string {
  return rawValue.trim().replace(/\/$/, '');
}

function parseBooleanEnv(
  envName: string,
  value: unknown,
  defaultValue: boolean,
  errors: string[]
): boolean {
  if (value === undefined || value === null || String(value).trim() === '') {
    return defaultValue;
  }

  const normalized = String(value).trim().toLowerCase();
  if (normalized === 'true' || normalized === '1' || normalized === 'yes') {
    return true;
  }

  if (normalized === 'false' || normalized === '0' || normalized === 'no') {
    return false;
  }

  errors.push(`${envName} must be true/false (received "${String(value)}").`);
  return defaultValue;
}

function parsePositiveNumberEnv(
  envName: string,
  value: unknown,
  defaultValue: number,
  errors: string[],
  allowZero = false
): number {
  if (value === undefined || value === null || String(value).trim() === '') {
    return defaultValue;
  }

  const parsed = Number(value);
  const min = allowZero ? 0 : 1;
  if (!Number.isFinite(parsed) || parsed < min) {
    errors.push(`${envName} must be a number >= ${min} (received "${String(value)}").`);
    return defaultValue;
  }

  return parsed;
}

function parseAbsoluteHttpUrl(
  envName: string,
  value: string | undefined,
  fallbackValue: string,
  errors: string[]
): string {
  if (!value || value.trim() === '') {
    return fallbackValue;
  }

  const normalized = normalizeUrlWithoutTrailingSlash(value);

  try {
    const url = new URL(normalized);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
      errors.push(`${envName} must start with http:// or https:// (received "${value}").`);
      return fallbackValue;
    }

    return normalized;
  } catch {
    errors.push(`${envName} must be a valid absolute URL (received "${value}").`);
    return fallbackValue;
  }
}

function parseTelemetryEndpoint(
  value: string | undefined,
  errors: string[]
): string | undefined {
  if (!value || value.trim() === '') {
    return undefined;
  }

  const normalized = value.trim();
  if (normalized.startsWith('/')) {
    return normalized;
  }

  try {
    const url = new URL(normalized);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
      errors.push(
        `VITE_TELEMETRY_ENDPOINT must be an absolute URL or path (received "${value}").`
      );
      return undefined;
    }
    return normalized;
  } catch {
    errors.push(
      `VITE_TELEMETRY_ENDPOINT must be an absolute URL or path (received "${value}").`
    );
    return undefined;
  }
}

function parseAbsoluteHttpUrlOrPath(
  envName: string,
  value: string | undefined,
  fallbackValue: string,
  errors: string[]
): string {
  if (!value || value.trim() === '') {
    return fallbackValue;
  }

  const normalized = value.trim();
  if (normalized.startsWith('/')) {
    return normalized;
  }

  try {
    const url = new URL(normalized);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
      errors.push(`${envName} must be an absolute HTTP(S) URL or path (received "${value}").`);
      return fallbackValue;
    }

    return normalized;
  } catch {
    errors.push(`${envName} must be an absolute HTTP(S) URL or path (received "${value}").`);
    return fallbackValue;
  }
}

function parseAppEnvironment(
  envValue: string | undefined,
  isDev: boolean,
  errors: string[]
): AppEnvironment {
  if (!envValue || envValue.trim() === '') {
    return isDev ? 'development' : 'production';
  }

  const normalized = envValue.trim().toLowerCase();
  if (
    VALID_APP_ENVIRONMENTS.includes(
      normalized as ValidAppEnvironment
    )
  ) {
    return normalized as AppEnvironment;
  }

  errors.push(
    `VITE_APP_ENV must be one of ${VALID_APP_ENVIRONMENTS.join(', ')} (received "${envValue}").`
  );
  return isDev ? 'development' : 'production';
}

function parseLogLevel(logLevelValue: string | undefined, errors: string[]): LogLevel {
  if (!logLevelValue || logLevelValue.trim() === '') {
    return DEFAULT_LOG_LEVEL;
  }

  const normalized = logLevelValue.trim().toLowerCase();
  if (VALID_LOG_LEVELS.includes(normalized as ValidLogLevel)) {
    return normalized as LogLevel;
  }

  errors.push(
    `VITE_LOG_LEVEL must be one of ${VALID_LOG_LEVELS.join(', ')} (received "${logLevelValue}").`
  );
  return DEFAULT_LOG_LEVEL;
}

function parseSymfoniApiMode(modeValue: string | undefined, errors: string[]): SymfoniApiMode {
  if (!modeValue || modeValue.trim() === '') {
    return 'remote';
  }

  const normalized = modeValue.trim().toLowerCase();
  if (normalized === 'mock' || normalized === 'remote') {
    return normalized;
  }

  errors.push(`VITE_SYMFONI_API_MODE must be "mock" or "remote" (received "${modeValue}").`);
  return 'remote';
}

function parseVariant(value: string | undefined, fallback: AppVariant): AppVariant {
  const normalized = value?.trim().toLowerCase();
  if (
    normalized === 'english' ||
    normalized === 'oslo-circular-fast-track' ||
    normalized === 'innovasjon-norge' ||
    normalized === 'nordic-smart-city-v2' ||
    normalized === 'oslo' ||
    normalized === 'geonorge' ||
    normalized === 'iga-geonorge' ||
    normalized === 'hmsreg'
  ) {
    return normalized;
  }
  return fallback;
}

function resolveRuntimeConfig(options: RuntimeInitOptions): RuntimeConfig {
  const errors: string[] = [];
  const warnings: string[] = [];
  const inferredVariant = parseVariant(import.meta.env.VITE_APP_VARIANT, options.variant);
  const hostname =
    typeof window !== 'undefined' ? window.location.hostname : '';
  const isLocalRuntime = hostname ? isLocalhost(hostname) : false;
  const useDevProxy = import.meta.env.DEV && isLocalRuntime;
  const appEnvironment = parseAppEnvironment(import.meta.env.VITE_APP_ENV, import.meta.env.DEV, errors);
  const symfoniApiMode = parseSymfoniApiMode(import.meta.env.VITE_SYMFONI_API_MODE, errors);
  const remoteBaseUrl = parseAbsoluteHttpUrl(
    'VITE_SYMFONI_BASE_URL',
    import.meta.env.VITE_SYMFONI_BASE_URL,
    DEFAULT_REMOTE_BASE_URL,
    errors
  );
  const geonorgeDemoUrl = parseAbsoluteHttpUrlOrPath(
    'VITE_GEONORGE_DEMO_URL',
    import.meta.env.VITE_GEONORGE_DEMO_URL,
    import.meta.env.DEV ? DEFAULT_DEV_GEONORGE_DEMO_URL : DEFAULT_BUILD_GEONORGE_DEMO_URL,
    errors
  );

  const telemetryEnabled = parseBooleanEnv(
    'VITE_TELEMETRY_ENABLED',
    import.meta.env.VITE_TELEMETRY_ENABLED,
    false,
    errors
  );
  const telemetryEndpoint = parseTelemetryEndpoint(import.meta.env.VITE_TELEMETRY_ENDPOINT, errors);
  const logLevel = parseLogLevel(import.meta.env.VITE_LOG_LEVEL, errors);
  const mockExpiresMs = parsePositiveNumberEnv(
    'VITE_SYMFONI_MOCK_EXPIRES_MS',
    import.meta.env.VITE_SYMFONI_MOCK_EXPIRES_MS,
    DEFAULT_MOCK_EXPIRES_MS,
    errors
  );
  const mockCompleteDelayMs = parsePositiveNumberEnv(
    'VITE_SYMFONI_MOCK_COMPLETE_DELAY_MS',
    import.meta.env.VITE_SYMFONI_MOCK_COMPLETE_DELAY_MS,
    DEFAULT_MOCK_COMPLETE_DELAY_MS,
    errors,
    true
  );
  const enableNgrokSkipHeader = parseBooleanEnv(
    'VITE_SYMFONI_ENABLE_NGROK_SKIP_HEADER',
    import.meta.env.VITE_SYMFONI_ENABLE_NGROK_SKIP_HEADER,
    false,
    errors
  );

  if (telemetryEnabled && !telemetryEndpoint) {
    warnings.push(
      'Telemetry is enabled but VITE_TELEMETRY_ENDPOINT is not set. Events will only be logged locally.'
    );
  }

  if (appEnvironment === 'production' && symfoniApiMode === 'mock') {
    warnings.push('Symfoni API mode is mock while VITE_APP_ENV=production.');
  }

  if (!import.meta.env.DEV && useDevProxy) {
    errors.push('Vite dev proxy cannot be used when import.meta.env.DEV is false.');
  }

  if (errors.length > 0) {
    throw new Error(`Invalid runtime environment:\n- ${errors.join('\n- ')}`);
  }

  const runtimeConfig: RuntimeConfig = {
    app: {
      variant: inferredVariant,
      locale: options.locale,
      environment: appEnvironment,
      version: String(import.meta.env.VITE_APP_VERSION || '0.0.0').trim() || '0.0.0',
      basePath: normalizeBasePath(import.meta.env.BASE_URL),
    },
    network: {
      useDevProxy,
      corsMode: useDevProxy ? 'vite-proxy' : 'direct',
      brregBasePath: BRREG_PROXY_BASE,
      enhetsregisterBasePath: ENHETSREGISTER_PROXY_BASE,
      stotteregisterSearchPath: STOTTEREGISTER_SEARCH_PATH,
      eAidRegisterApiBasePath: EAID_REGISTER_API_BASE,
    },
    symfoni: {
      apiMode: symfoniApiMode,
      remoteBaseUrl,
      demoApiBase: useDevProxy ? LOCAL_DEV_API_PROXY_BASE : `${remoteBaseUrl}/api/demo`,
      enableNgrokSkipHeader,
      mockExpiresMs,
      mockCompleteDelayMs,
    },
    geonorge: {
      demoUrl: geonorgeDemoUrl,
    },
    observability: {
      telemetryEnabled,
      telemetryEndpoint,
      logLevel,
    },
    warnings,
  };

  return runtimeConfig;
}

function getFallbackVariant(): AppVariant {
  return parseVariant(import.meta.env.VITE_APP_VARIANT, 'oslo');
}

function getFallbackLocale(variant: AppVariant): string {
  return variant === 'english' || variant === 'oslo-circular-fast-track' ? 'en' : 'nb';
}

export function initializeRuntimeConfig(options: RuntimeInitOptions): RuntimeConfig {
  activeRuntimeConfig = resolveRuntimeConfig(options);
  return activeRuntimeConfig;
}

export function getRuntimeConfig(): RuntimeConfig {
  if (activeRuntimeConfig) {
    return activeRuntimeConfig;
  }

  const variant = getFallbackVariant();
  return initializeRuntimeConfig({
    variant,
    locale: getFallbackLocale(variant),
  });
}

export function resolveAppPath(path: string): string {
  const normalizedPath = normalizeAppPath(path);
  const { basePath } = getRuntimeConfig().app;

  if (basePath === '/') {
    return normalizedPath;
  }

  return `${basePath.replace(/\/$/, '')}${normalizedPath}`;
}

export function resetRuntimeConfigForTests(): void {
  activeRuntimeConfig = null;
}
